import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, ExternalLink, Download, Upload } from "lucide-react";
import { api, adminAuthHeaders } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { SECTIONS, EMPTY_BRAND, EMPTY_PAGES } from "./sections/constants";
import BrandSection from "./sections/BrandSection";
import HomeSection from "./sections/HomeSection";
import PhonePlansSection from "./sections/PhonePlansSection";
import MiPlansSection from "./sections/MiPlansSection";
import CodesSection from "./sections/CodesSection";
import InternetPageSection from "./sections/InternetPageSection";
import RegulaminSection from "./sections/RegulaminSection";
import LogoSection from "./sections/LogoSection";
import OtherPagesSection from "./sections/OtherPagesSection";
import RodoSection from "./sections/RodoSection";
import MenuSection from "./sections/MenuSection";
import FloatingCallSection from "./sections/FloatingCallSection";
import DiscountsSection from "./sections/DiscountsSection";
import PlacementsSection from "./sections/PlacementsSection";
import FooterSection from "./sections/FooterSection";
import CookiePolicySection from "./sections/CookiePolicySection";

export default function ContentEditor() {
  const { refresh } = useContent();
  const [phonePlans, setPhonePlans] = useState([]);
  const [miPlans, setMiPlans] = useState([]);
  const [codes, setCodes] = useState([]);
  const [brand, setBrand] = useState(EMPTY_BRAND);
  const [pages, setPages] = useState(EMPTY_PAGES);
  const [imagePlacements, setImagePlacements] = useState([]);
  const [section, setSection] = useState("home");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        setPhonePlans(data.phone_plans || []);
        setMiPlans(data.mobile_internet_plans || []);
        setCodes(data.internet_codes || []);
        setBrand({
          ...EMPTY_BRAND,
          ...(data.brand || {}),
          hero: { ...EMPTY_BRAND.hero, ...(data.brand?.hero || {}) },
          hero_eyebrow_style: { ...EMPTY_BRAND.hero_eyebrow_style, ...(data.brand?.hero_eyebrow_style || {}) },
          hero_title_style: { ...EMPTY_BRAND.hero_title_style, ...(data.brand?.hero_title_style || {}) },
          countdown: { ...EMPTY_BRAND.countdown, ...(data.brand?.countdown || {}) },
          sales_lock_enabled: data.brand?.sales_lock_enabled !== undefined ? data.brand.sales_lock_enabled : true,
          contact: { ...EMPTY_BRAND.contact, ...(data.brand?.contact || {}) },
          coverage: { ...EMPTY_BRAND.coverage, ...(data.brand?.coverage || {}) },
          plans_heading: { ...EMPTY_BRAND.plans_heading, ...(data.brand?.plans_heading || {}) },
          features: Array.isArray(data.brand?.features) ? data.brand.features : [],
          top_features: Array.isArray(data.brand?.top_features) ? data.brand.top_features : [],
          cta_home: {
            ...EMPTY_BRAND.cta_home,
            ...(data.brand?.cta_home || {}),
            primary_btn: { ...EMPTY_BRAND.cta_home.primary_btn, ...(data.brand?.cta_home?.primary_btn || {}) },
            secondary_btn: { ...EMPTY_BRAND.cta_home.secondary_btn, ...(data.brand?.cta_home?.secondary_btn || {}) },
          },
          floating_call: { ...EMPTY_BRAND.floating_call, ...(data.brand?.floating_call || {}) },
          menu: {
            ...EMPTY_BRAND.menu,
            ...(data.brand?.menu || {}),
            labels: { ...EMPTY_BRAND.menu.labels, ...(data.brand?.menu?.labels || {}) },
          },
        });
        setPages({
          internet: { ...EMPTY_PAGES.internet, ...(data.pages?.internet || {}), bullets: data.pages?.internet?.bullets || [] },
          regulamin: { ...EMPTY_PAGES.regulamin, ...(data.pages?.regulamin || {}) },
          extra_internet: { ...EMPTY_PAGES.extra_internet, ...(data.pages?.extra_internet || {}) },
          contact: { ...EMPTY_PAGES.contact, ...(data.pages?.contact || {}) },
          order_sim: { ...EMPTY_PAGES.order_sim, ...(data.pages?.order_sim || {}) },
          port_number: { ...EMPTY_PAGES.port_number, ...(data.pages?.port_number || {}) },
          cookie_policy: { ...EMPTY_PAGES.cookie_policy, ...(data.pages?.cookie_policy || {}) },
          rodo: { ...EMPTY_PAGES.rodo, ...(data.pages?.rodo || {}) },
          payment: { ...EMPTY_PAGES.payment, ...(data.pages?.payment || {}) },
        });
        setImagePlacements(Array.isArray(data.image_placements) ? data.image_placements : []);
      } catch {
        toast.error("Nie udało się wczytać treści");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updatePhone = (i, field, value) =>
    setPhonePlans((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  const updateMi = (i, field, value) =>
    setMiPlans((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  const updateCode = (i, field, value) =>
    setCodes((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));

  const addPhone = () =>
    setPhonePlans((prev) => [
      ...prev,
      { id: `plan-${Date.now()}`, name: "Nowy pakiet", price: 0, period: "/mies.", tagline: "", description: "", features: [], featured: false },
    ]);
  const addMi = () =>
    setMiPlans((prev) => [...prev, { id: `mi-${Date.now()}`, name: "Nowy pakiet", price: 0, period: "/mies." }]);
  const addCode = () => setCodes((prev) => [...prev, { code: "", size: "", price: 0 }]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        phone_plans: phonePlans.map((p) => ({
          ...p,
          price: Number(p.price) || 0,
          features: Array.isArray(p.features)
            ? p.features
            : String(p.features || "").split("\n").map((f) => f.trim()).filter(Boolean),
        })),
        mobile_internet_plans: miPlans.map((p) => ({ ...p, price: Number(p.price) || 0 })),
        internet_codes: codes.map((c) => ({ ...c, price: Number(c.price) || 0 })),
        brand,
        pages,
        image_placements: imagePlacements,
      };
      await api.put("/admin/content", payload, { headers: adminAuthHeaders() });
      toast.success("Zapisano zmiany");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/logo", fd, {
        headers: { ...adminAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setBrand((b) => ({ ...b, logo_url: data.logo_url }));
      toast.success("Logo wgrane");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd wgrywania");
    } finally {
      setUploadingLogo(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/admin/content/backup", { headers: adminAuthHeaders() });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `rmobile-kopia-tresci-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Kopia zapasowa pobrana");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd pobierania kopii");
    }
  };

  const restoreBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("Czy na pewno chcesz przywrócić treści z tego pliku? Bieżące teksty zostaną nadpisane.")) return;
    setRestoring(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      await api.post("/admin/content/restore", json, { headers: adminAuthHeaders() });
      toast.success("Kopia zapasowa przywrócona");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd wgrywania kopii (nieprawidłowy plik JSON?)");
    } finally {
      setRestoring(false);
      if (restoreInputRef.current) restoreInputRef.current.value = "";
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Wczytywanie…</div>;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const activeSection = SECTIONS.find((s) => s.id === section);

  return (
    <div data-testid="content-editor" className="p-6 space-y-6">
      <div className="rounded-xl border border-slate-200 p-4 bg-white flex items-center gap-3 flex-wrap sticky top-2 z-10 shadow-sm">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Edytuj:</span>
        <select data-testid="section-select" value={section} onChange={(e) => setSection(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#1f3a8a]">
          {SECTIONS.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        {activeSection?.preview && (
          <a
            data-testid="preview-link"
            href={activeSection.preview}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1f3a8a] hover:text-[#dc2626] px-3 py-2 rounded-lg border border-slate-200 hover:border-[#dc2626] transition"
            title="Otwórz podgląd strony w nowej karcie"
          >
            <ExternalLink size={14} /> Podejrzyj stronę
          </a>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button
            data-testid="backup-download"
            type="button"
            onClick={downloadBackup}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-[#1f3a8a] px-3 py-2 rounded-lg border border-slate-200 hover:border-[#1f3a8a] transition"
            title="Pobierz pełną kopię zapasową treści (JSON)"
          >
            <Download size={14} /> Kopia zapasowa
          </button>
          <input
            ref={restoreInputRef}
            data-testid="backup-restore-input"
            type="file"
            accept="application/json,.json"
            onChange={(e) => restoreBackup(e.target.files?.[0])}
            className="hidden"
          />
          <button
            data-testid="backup-restore"
            type="button"
            onClick={() => restoreInputRef.current?.click()}
            disabled={restoring}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-[#dc2626] px-3 py-2 rounded-lg border border-slate-200 hover:border-[#dc2626] transition disabled:opacity-60"
            title="Wgraj wcześniej pobraną kopię zapasową i przywróć treści"
          >
            <Upload size={14} /> {restoring ? "Wgrywanie…" : "Przywróć z pliku"}
          </button>
        </div>
      </div>

      {section === "marka" && <BrandSection brand={brand} setBrand={setBrand} />}
      {section === "menu" && <MenuSection brand={brand} setBrand={setBrand} />}
      {section === "call" && <FloatingCallSection brand={brand} setBrand={setBrand} />}
      {section === "discounts" && <DiscountsSection brand={brand} setBrand={setBrand} phone_plans={phonePlans} mobile_internet_plans={miPlans} />}
      {section === "home" && <HomeSection brand={brand} setBrand={setBrand} />}
      {section === "plany" && <PhonePlansSection phonePlans={phonePlans} setPhonePlans={setPhonePlans} updatePhone={updatePhone} addPhone={addPhone} />}
      {section === "mi" && <MiPlansSection miPlans={miPlans} setMiPlans={setMiPlans} updateMi={updateMi} addMi={addMi} />}
      {section === "kody" && <CodesSection codes={codes} setCodes={setCodes} updateCode={updateCode} addCode={addCode} pages={pages} setPages={setPages} />}
      {section === "internet" && <InternetPageSection pages={pages} setPages={setPages} />}
      {section === "podstrony" && <OtherPagesSection pages={pages} setPages={setPages} />}
      {section === "regulamin" && <RegulaminSection pages={pages} setPages={setPages} />}
      {section === "rodo" && <RodoSection pages={pages} setPages={setPages} />}
      {section === "cookies" && <CookiePolicySection pages={pages} setPages={setPages} />}
      {section === "footer" && <FooterSection brand={brand} setBrand={setBrand} />}
      {section === "placements" && (
        <PlacementsSection
          imagePlacements={imagePlacements}
          setImagePlacements={setImagePlacements}
        />
      )}
      {section === "logo" && <LogoSection brand={brand} setBrand={setBrand} uploadLogo={uploadLogo} uploadingLogo={uploadingLogo} backendUrl={BACKEND_URL} />}

      <button
        data-testid="save-content"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white font-bold px-6 py-3 rounded-full shadow-lg shadow-[#dc2626]/30 transition disabled:opacity-60"
      >
        <Save size={18} /> {saving ? "Zapisywanie…" : "Zapisz zmiany"}
      </button>
    </div>
  );
}
